// frontend/src/components/announcements/AnnouncementHistory.jsx
// Component for users to view their announcement history

import React, { useState, useEffect, useCallback } from "react";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";
import "./announcements.css";

const AnnouncementHistory = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [expandedId, setExpandedId] = useState(null);

  // Fetch announcement history
  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
      });

      const response = await secureAxios.get(
        `/api/announcements/history?${params}`
      );
      setAnnouncements(response.data.announcements || []);
      setPagination((prev) => ({
        ...prev,
        ...response.data.pagination,
      }));
    } catch (err) {
      console.error("Error fetching announcement history:", err);
      toast.error("Failed to load announcement history");
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Get priority badge
  const getPriorityBadge = (priority) => {
    const badges = {
      urgent: { class: "bg-danger", label: "Urgent" },
      high: { class: "bg-warning text-dark", label: "High" },
      normal: { class: "bg-info", label: "Normal" },
      low: { class: "bg-secondary", label: "Low" },
    };
    return badges[priority] || badges.normal;
  };

  // Get status badge
  const getStatusBadge = (status) => {
    if (!status) {
      return {
        class: "bg-light text-dark",
        label: "Not Seen",
        icon: "bi-eye-slash",
      };
    }
    const statuses = {
      dismissed: {
        class: "bg-secondary",
        label: "Dismissed",
        icon: "bi-check-circle",
      },
      viewed: { class: "bg-success", label: "Viewed", icon: "bi-eye" },
      read_later: {
        class: "bg-warning text-dark",
        label: "Snoozed",
        icon: "bi-clock",
      },
    };
    return (
      statuses[status] || {
        class: "bg-light text-dark",
        label: status,
        icon: "bi-question",
      }
    );
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Check if announcement is expired
  const isExpired = (announcement) => {
    if (!announcement.end_date) return false;
    return new Date(announcement.end_date) < new Date();
  };

  return (
    <div className="container mt-4 px-3 px-md-4 px-lg-5">
      {/* Header */}
      <div className="mb-4">
        <h2>
          <i className="bi bi-clock-history me-2"></i>
          Announcement History
        </h2>
        <p className="text-muted mb-0">View all past announcements</p>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading announcements...</p>
        </div>
      ) : announcements.length === 0 ? (
        /* Empty State */
        <div className="text-center py-5">
          <i className="bi bi-inbox display-1 text-muted"></i>
          <h4 className="mt-3 text-muted">No Announcements</h4>
          <p className="text-muted">There are no announcements to display.</p>
        </div>
      ) : (
        /* Announcements List */
        <>
          {announcements.map((announcement) => {
            const priority = getPriorityBadge(announcement.priority);
            const status = getStatusBadge(announcement.user_status);
            const expired = isExpired(announcement);
            const isExpanded = expandedId === announcement.announcement_id;

            return (
              <div
                key={announcement.announcement_id}
                className={`announcement-history-item ${
                  announcement.user_status === "dismissed" ? "dismissed" : ""
                }`}
              >
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                      <h5 className="mb-0">{announcement.title}</h5>
                      <span className={`badge ${priority.class}`}>
                        {priority.label}
                      </span>
                      <span
                        className={`status-indicator ${
                          announcement.user_status || "not-seen"
                        }`}
                      >
                        <i className={`bi ${status.icon} me-1`}></i>
                        {status.label}
                      </span>
                      {expired && (
                        <span className="badge bg-secondary">Expired</span>
                      )}
                      {!announcement.is_active && (
                        <span className="badge bg-secondary">Inactive</span>
                      )}
                    </div>

                    <div
                      className={`announcement-content ${
                        isExpanded ? "" : "text-truncate"
                      }`}
                      style={{
                        maxHeight: isExpanded ? "none" : "3em",
                        overflow: "hidden",
                      }}
                    >
                      {announcement.content}
                    </div>

                    {announcement.content.length > 150 && (
                      <button
                        className="btn btn-link btn-sm p-0 mt-1"
                        onClick={() =>
                          setExpandedId(
                            isExpanded ? null : announcement.announcement_id
                          )
                        }
                      >
                        {isExpanded ? (
                          <>
                            <i className="bi bi-chevron-up me-1"></i>
                            Show less
                          </>
                        ) : (
                          <>
                            <i className="bi bi-chevron-down me-1"></i>
                            Show more
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>

                <div className="mt-2 text-muted small d-flex gap-3 flex-wrap">
                  <span>
                    <i className="bi bi-calendar3 me-1"></i>
                    Posted: {formatDate(announcement.created_at)}
                  </span>
                  {announcement.end_date && (
                    <span>
                      <i className="bi bi-calendar-x me-1"></i>
                      {expired ? "Expired" : "Expires"}:{" "}
                      {formatDate(announcement.end_date)}
                    </span>
                  )}
                  {announcement.created_by_name && (
                    <span>
                      <i className="bi bi-person me-1"></i>
                      By: {announcement.created_by_name}
                    </span>
                  )}
                  {announcement.user_action_at && (
                    <span>
                      <i className="bi bi-clock me-1"></i>
                      {announcement.user_status === "dismissed"
                        ? "Dismissed"
                        : "Seen"}
                      : {formatDate(announcement.user_action_at)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <nav className="mt-4">
              <ul className="pagination justify-content-center">
                <li
                  className={`page-item ${
                    pagination.page === 1 ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page - 1,
                      }))
                    }
                    disabled={pagination.page === 1}
                  >
                    Previous
                  </button>
                </li>
                {[...Array(Math.min(pagination.totalPages, 5))].map(
                  (_, idx) => {
                    // Show pages around current page
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = idx + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = idx + 1;
                    } else if (pagination.page >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + idx;
                    } else {
                      pageNum = pagination.page - 2 + idx;
                    }

                    return (
                      <li
                        key={pageNum}
                        className={`page-item ${
                          pagination.page === pageNum ? "active" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() =>
                            setPagination((prev) => ({
                              ...prev,
                              page: pageNum,
                            }))
                          }
                        >
                          {pageNum}
                        </button>
                      </li>
                    );
                  }
                )}
                <li
                  className={`page-item ${
                    pagination.page === pagination.totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page + 1,
                      }))
                    }
                    disabled={pagination.page === pagination.totalPages}
                  >
                    Next
                  </button>
                </li>
              </ul>
              <div className="text-center text-muted small">
                Page {pagination.page} of {pagination.totalPages} (
                {pagination.total} total)
              </div>
            </nav>
          )}
        </>
      )}
    </div>
  );
};

export default AnnouncementHistory;
